import { type CSSProperties, useCallback } from 'react';

import { ArrowClockwise, ArrowCounterClockwise } from '@phosphor-icons/react/dist/ssr';

import { cn } from '@/utils';
import { formatAudioPlayerTime } from '@/utils/formatAudioPlayerTime';
import { useAudioPlayer } from '@/utils/useAudioPlayer';

import * as styles from './AudioPlayer.css';
import { EmailPlayButtonReference } from './EmailPlayButtonReference';
import PlayButton from './PlayButton';

/** Play icon asset URLs by text tone — matches Swarm `PODCAST_EMBED_PLAY_ICON_*`. */
const PLAY_ICON_URL = {
  light: 'https://media.beehiiv.com/static_assets/bhv_podEp_play_bk.png',
  dark: 'https://media.beehiiv.com/static_assets/bhv_podEp_play_wh.png',
} as const;

interface Props {
  audioUrl: string;
  audioType?: string;
  duration?: number;
  durationDisplay?: string | null;
  primaryColor?: CSSProperties['backgroundColor'];
  textColor?: CSSProperties['color'];
  trackColor?: CSSProperties['backgroundColor'];
  trackProgressColor?: CSSProperties['backgroundColor'];
  iconColor: CSSProperties['color'];
  showRewind?: boolean;
  showForward?: boolean;
  rewindSeconds?: number;
  forwardSeconds?: number;
  playButtonSize: number;
  skipButtonSize?: number;
  progressBarHeight?: number;
  onPlayAttempt?: () => void;
  isPlaybackLocked?: boolean;
  audioElementId?: string;
  showEmailPlayReference?: boolean;
  /** Tone key into `PLAY_ICON_URL` for the static email play icon (`light` | `dark`). */
  emailPlayIconTone?: keyof typeof PLAY_ICON_URL;
  /** Fill color for static email-play reference button (hex string). */
  emailPlayButtonColor?: string;
  /** Reduces vertical padding around controls for compact live rendering. */
  compactVerticalPadding?: boolean;
}

export function AudioPlayer({
  audioUrl,
  audioType = 'audio/mpeg',
  duration: knownDuration,
  durationDisplay,
  primaryColor,
  textColor,
  trackColor,
  trackProgressColor,
  iconColor,
  showRewind,
  showForward,
  rewindSeconds,
  forwardSeconds,
  playButtonSize,
  skipButtonSize,
  progressBarHeight,
  onPlayAttempt,
  isPlaybackLocked,
  audioElementId,
  showEmailPlayReference = false,
  emailPlayIconTone,
  emailPlayButtonColor,
  compactVerticalPadding = false,
}: Props) {
  const {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  } = useAudioPlayer({ rewindSeconds, forwardSeconds, knownDuration });
  const totalDurationDisplay = durationDisplay || '00:00';

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      seekTo(ratio);
    },
    [seekTo],
  );

  const handleProgressKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!duration) return;
      const step = 0.02;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        seekTo(Math.min(1, currentTime / duration + step));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        seekTo(Math.max(0, currentTime / duration - step));
      }
    },
    [duration, currentTime, seekTo],
  );

  const progressRow = (
    <div
      className={styles.progressRow}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, width: '100%' }}
    >
      <span
        className={styles.timeLabel}
        style={{
          color: textColor || undefined,
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: '12px',
          lineHeight: '14px',
          opacity: 0.6,
        }}
      >
        {formatAudioPlayerTime(currentTime)}
      </span>

      <div
        className={styles.progressBar}
        style={{ backgroundColor: trackColor, height: progressBarHeight, flex: 1, minWidth: 0, width: '100%' }}
        onClick={handleProgressClick}
        onKeyDown={handleProgressKeyDown}
        role="slider"
        aria-valuenow={currentTime}
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-label="Seek audio"
        tabIndex={0}
      >
        <div
          className={styles.progressFill}
          style={{
            width: `${progress}%`,
            backgroundColor: trackProgressColor || primaryColor,
          }}
        />
      </div>

      <span
        className={styles.timeLabel}
        style={{
          color: textColor || undefined,
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: '12px',
          lineHeight: '14px',
          opacity: 0.6,
        }}
      >
        {totalDurationDisplay}
      </span>
    </div>
  );

  return (
    <div
      className={cn(
        styles.playerWrapper,
        compactVerticalPadding && styles.playerWrapperCompactPadding,
        showEmailPlayReference && styles.playerWrapperEmailTextColumn,
        isPlaybackLocked && 'pointer-events-none cursor-not-allowed',
      )}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        width: '100%',
        boxSizing: 'border-box',
        ...(compactVerticalPadding ? { padding: '6px 0' } : {}),
      }}
    >
      <div className={styles.playerContent} style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div
          className={cn(
            styles.controlsRow,
            showEmailPlayReference ? styles.controlsRowEmailReferenceColumns : undefined,
          )}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexWrap: 'nowrap',
            width: '100%',
            minWidth: 0,
          }}
        >
          {showRewind && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleRewind}
              disabled={!isReady}
              aria-label={`Rewind ${rewindSeconds} seconds`}
            >
              <ArrowCounterClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {rewindSeconds}
              </span>
            </button>
          )}

          {showEmailPlayReference ? (
            <>
              <div className={styles.emailPlayReferenceCell}>
                <EmailPlayButtonReference
                  backgroundColor={emailPlayButtonColor}
                  iconUrl={emailPlayIconTone ? PLAY_ICON_URL[emailPlayIconTone] : undefined}
                />
              </div>
              <div className={styles.emailPlayRowGutter} aria-hidden />
              <div className={styles.emailPlayProgressColumn}>{progressRow}</div>
            </>
          ) : (
            <>
              {!isPlaybackLocked ? (
                <PlayButton
                  isReady={isReady}
                  isPlaybackLocked={isPlaybackLocked}
                  isPlaying={isPlaying}
                  isBuffering={isBuffering}
                  playButtonSize={playButtonSize}
                  iconColor={iconColor}
                  togglePlay={togglePlay}
                  onPlayAttempt={onPlayAttempt}
                />
              ) : null}
              {progressRow}
            </>
          )}

          {showForward && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleForward}
              disabled={!isReady}
              aria-label={`Forward ${forwardSeconds} seconds`}
            >
              <ArrowClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {forwardSeconds}
              </span>
            </button>
          )}
        </div>
      </div>

      {audioUrl && (
        <audio ref={audioRef} id={audioElementId} preload={srcAttached ? 'metadata' : 'none'}>
          <source src={audioUrl} type={audioType} />
          <track kind="captions" src="" label="No captions available" />
        </audio>
      )}
    </div>
  );
}
